import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import type { ThunkAction, ThunkExtras, GetState, APIError, Dispatch } from '../../types/app.types';
import type { V2RefillMedsResponse, V2RefillMeds, V2AddCartItem, V2RefillMedItem } from '../../types/api-v2.types';

/**
 * types
 */
export type OrderMedsActionSuccess = {
  type: 'order/MEDICATIONS_SUCCESS';
  priceCategory: string;
  meds: Array<V2RefillMedItem>;
};
export type OrderMedsActionError = {
  type: 'order/MEDICATIONS_ERROR';
} & APIError;
export type UpdateInCartStatus = {
  type: 'order/UPDATE_IN_CART_STATUS';
  items: Array<V2AddCartItem> | null | undefined;
  cartItemId: string | null | undefined;
  inCart: boolean;
};
export type OrderMedsAction = OrderMedsActionSuccess | OrderMedsActionError | UpdateInCartStatus;

/**
 * Actions
 */
export function optimisticallyUpdateInCartStatus(
  items: Array<V2AddCartItem> | null | undefined,
  cartItemId: string | null | undefined,
  inCart: boolean
): UpdateInCartStatus {
  return {
    type: 'order/UPDATE_IN_CART_STATUS',
    items,
    inCart,
    cartItemId,
  };
}
let getOrderMedsCanceler: CancelTokenSource;
export function getOrderMeds(): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    if (getOrderMedsCanceler) {
      getOrderMedsCanceler.cancel(); // only handle one in-flight request at a time
    }

    getOrderMedsCanceler = axios.CancelToken.source();
    return extras.blinkAPIClient
      .get(`/v2/order_meds`, {
        cancelToken: getOrderMedsCanceler.token,
      })
      .then((res) => res.data)
      .then((data: V2RefillMedsResponse): V2RefillMeds => data.result)
      .then((data: V2RefillMeds) =>
        dispatch({
          type: 'order/MEDICATIONS_SUCCESS',
          meds: data.items,
          priceCategory: data.priceCategory,
        })
      )
      .catch((err: APIError) => {
        if (!err.isCancel) {
          return dispatch({
            type: 'order/MEDICATIONS_ERROR',
            ...err,
          });
        }
      });
  };
}

/**
 * Selectors
 */
const ACTIVE_STATES = ['opened', 'pbm_authorized', 'card_authorized', 'flagged', 'fraud', 'active', 'ordered'];
export const isPurchaseActive = (medicationStatus?: string): boolean => ACTIVE_STATES.includes(medicationStatus);

/*************
 * Utility
 *************/
function setMedCartStatus(
  meds: Array<V2RefillMedItem>,
  inCart: boolean,
  items: Array<V2AddCartItem> | null | undefined,
  cartItemId?: string | null | undefined
): Array<V2RefillMedItem> {
  const modifiedMeds: Array<V2RefillMedItem> = [...meds];

  if (items && items.length > 0) {
    items.forEach((item: any) => {
      modifiedMeds.forEach((med: any) => {
        if (med.medId === item.medId) med.isInCart = inCart;
      });
    });
  } else if (cartItemId) {
    modifiedMeds.forEach((med: any) => {
      if (med.cartItemId === cartItemId) med.isInCart = inCart;
    });
  }

  return modifiedMeds;
}

export type OrderMedicationState = {
  meds: Array<V2RefillMedItem>;
  priceCategory: string;
};
const initialState = {
  meds: [],
  priceCategory: 'edlp',
};
/**
 * Reducer
 */

export default function accountReducer(
  state: OrderMedicationState = initialState,
  action: OrderMedsAction
): OrderMedicationState {
  switch (action.type) {
    case 'order/MEDICATIONS_SUCCESS':
      return {
        meds: action.meds,
        priceCategory: action.priceCategory,
      };

    case 'order/UPDATE_IN_CART_STATUS':
      if (action.items && action.items.length > 0) {
        return { ...state, meds: setMedCartStatus(state.meds, action.inCart, action.items) };
      } else if (action.cartItemId) {
        return { ...state, meds: setMedCartStatus(state.meds, action.inCart, null, action.cartItemId) };
      } else {
        return state;
      }

    default:
      return state;
  }
}
